// TS twin of iOS WheelDetectionKit `WheelSizePinch` (0.7.0, ?use_ios_ui).
//
// One pinch on one wheel, fingers down to fingers up. The fingers ask for an
// edge-to-edge size of (start + offset) × pinch scale. The wheel is DRAWN
// there (`displayInches`, continuous, clamped to the list's ends); the size
// COMMITTED (`shown`) steps through the listed sizes with a 20% margin past
// each midpoint; on release the wheel settles on `nearestSize`.

import { GLB_LIP_ALLOWANCE_IN } from '@/models/base-diameter'
import { nearest } from '@/models/wheel-size-options'

/** Share of the gap between two sizes the fingers must ask past the
 *  midpoint before the committed size steps. Fingers jitter by ~1%. */
export const PINCH_HYSTERESIS = 0.2

export class WheelSizePinch {
  /** The listed size committed now. */
  shown: number
  /** Where the wheel is drawn while the fingers are down. */
  displayInches: number

  private readonly sizes: readonly number[]
  private readonly offset: number
  private startSize: number
  private startScale = 1

  constructor({
    sizes,
    shown,
    edgeToEdgeOffsetInches = GLB_LIP_ALLOWANCE_IN,
  }: {
    /** The choice's sizes, smallest first. */
    sizes: readonly number[]
    /** The wheel's size when the fingers landed, one of `sizes`. */
    shown: number
    edgeToEdgeOffsetInches?: number
  }) {
    this.sizes = sizes
    this.shown = shown
    this.displayInches = shown
    this.offset = edgeToEdgeOffsetInches
    this.startSize = shown
  }

  /** Where the wheel settles when the fingers lift: plain nearest, tie → smaller. */
  get nearestSize(): number {
    return nearest(this.sizes, this.displayInches) ?? this.shown
  }

  /** @param pinchScale cumulative since the fingers landed.
   *  @returns true when the committed size stepped. */
  update(pinchScale: number): boolean {
    if (!Number.isFinite(pinchScale) || pinchScale <= 0) return false
    const smallest = this.sizes[0]
    const largest = this.sizes[this.sizes.length - 1]
    if (smallest == null || largest == null) return false
    const relativeScale = pinchScale / this.startScale
    const next = stepPinchSize({
      sizes: this.sizes,
      shown: this.shown,
      start: this.startSize,
      pinchScale: relativeScale,
      edgeToEdgeOffsetInches: this.offset,
    })
    const requested = (this.startSize + this.offset) * relativeScale
    this.displayInches = Math.min(Math.max(requested - this.offset, smallest), largest)
    // Past an end, measure from that end so turning back answers at once.
    if (requested < smallest + this.offset || requested > largest + this.offset) {
      this.startSize = next
      this.startScale = pinchScale
    }
    const stepped = next !== this.shown
    this.shown = next
    return stepped
  }
}

/** The listed size a pinch commits: from `shown`, up while the fingers ask
 *  past the midpoint above by the margin, down likewise; repeats, so a fast
 *  pinch crosses several sizes in one update; stops at the ends. */
export function stepPinchSize({
  sizes,
  shown,
  start,
  pinchScale,
  edgeToEdgeOffsetInches = GLB_LIP_ALLOWANCE_IN,
}: {
  sizes: readonly number[]
  shown: number
  start: number
  pinchScale: number
  edgeToEdgeOffsetInches?: number
}): number {
  let index = sizes.indexOf(shown)
  if (index < 0) return shown
  const requested = (start + edgeToEdgeOffsetInches) * pinchScale
  const edge = (i: number) => sizes[i]! + edgeToEdgeOffsetInches
  while (index + 1 < sizes.length) {
    const gap = edge(index + 1) - edge(index)
    const midpoint = (edge(index) + edge(index + 1)) / 2
    if (!(requested > midpoint + PINCH_HYSTERESIS * gap)) break
    index += 1
  }
  while (index > 0) {
    const gap = edge(index) - edge(index - 1)
    const midpoint = (edge(index - 1) + edge(index)) / 2
    if (!(requested < midpoint - PINCH_HYSTERESIS * gap)) break
    index -= 1
  }
  return sizes[index]!
}
